import { useEffect } from 'react';
import { CriticalityLevel, GovernanceTask, Tool } from '../types';

interface InitialDataDeps {
  companyId: string | null;
  loadTools: () => Promise<Tool[]>;
  loadEmployees: () => Promise<unknown>;
  loadNotifications: () => Promise<unknown>;
  loadTraining: () => Promise<unknown>;
  loadSettings: () => Promise<{ criticalityLevels?: CriticalityLevel[] }>;
  loadAudit: () => Promise<unknown>;
  loadBilling: () => Promise<unknown>;
  loadGovernance: () => Promise<GovernanceTask[]>;
  syncGovernanceFromTools: (
    tools: Tool[],
    existingTasks: GovernanceTask[],
    criticalityLevels?: CriticalityLevel[]
  ) => Promise<GovernanceTask[]>;
}

export function useInitialAppData({
  companyId,
  loadTools,
  loadEmployees,
  loadNotifications,
  loadTraining,
  loadSettings,
  loadAudit,
  loadBilling,
  loadGovernance,
  syncGovernanceFromTools,
}: InitialDataDeps): void {
  useEffect(() => {
    if (!companyId) return;

    const loadData = async () => {
      try {
        const loadedTools = await loadTools();
        await loadEmployees();
        await loadNotifications();
        await loadTraining();
        const loadedSettings = await loadSettings();
        await loadAudit();
        await loadBilling();
        const governanceTasks = await loadGovernance();
        await syncGovernanceFromTools(
          loadedTools,
          Array.isArray(governanceTasks) ? governanceTasks : [],
          loadedSettings?.criticalityLevels
        );
      } catch {
        // Initial loading errors are exposed by each domain hook.
      }
    };

    loadData();
  }, [
    companyId,
    loadTools,
    loadEmployees,
    loadNotifications,
    loadTraining,
    loadSettings,
    loadAudit,
    loadBilling,
    loadGovernance,
    syncGovernanceFromTools,
  ]);
}
